import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import "./Timesheets.css";
import { toast } from "react-toastify";


const Timesheets = ({ employee, onTimesheetUpdate }) => {
  const [employeeName, setEmployeeName] = useState("");
  const [empId, setEmpId] = useState("");
  const [date, setDate] = useState("");
  const [hours, setHours] = useState("");
  const [task, setTask] = useState("");
  const [status, setStatus] = useState("Not Started");
  const [extraFields, setExtraFields] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState({ name: "", task: "", date: "", status: "" });
  const [activeTab, setActiveTab] = useState("submission");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch employee info
  useEffect(() => {
    if (!employee) return;
    setEmployeeName(employee.name || "Unknown");
    setEmpId(employee.emp_id || employee._id || "Unknown");
  }, [employee]);

  // Fetch timesheets
// Fix the fetchTimesheets function in your React component
const fetchTimesheets = async () => {
  if (!empId) return;
  try {
    // Use the emp_id endpoint instead of employee endpoint
    const res = await axios.get(`${API_BASE_URL}/api/timesheets/emp_id/${empId}`);
    const normalized = res.data.map(t => ({
      ...t,
      status: t.status || "Not Started",
      extraFields: t.extraFields?.map(f => {
        if (f.type === "file" && f.value && typeof f.value !== "string") {
          return { ...f, value: f.value.url || "" };
        }
        return f;
      }) || []
    }));
    setTimesheets(normalized);
    setLoading(false);
  } catch (err) {
    console.error("Error fetching timesheets:", err);
    // Fallback to employee endpoint if emp_id fails
    try {
      const res = await axios.get(`${API_BASE_URL}/api/timesheets/employee/${empId}`);
      const normalized = res.data.map(t => ({
        ...t,
        status: t.status || "Not Started",
        extraFields: t.extraFields?.map(f => {
          if (f.type === "file" && f.value && typeof f.value !== "string") {
            return { ...f, value: f.value.url || "" };
          }
          return f;
        }) || []
      }));
      setTimesheets(normalized);
    } catch (fallbackErr) {
      console.error("Fallback also failed:", fallbackErr);
    }
    setLoading(false);
  }
};

  useEffect(() => {
    fetchTimesheets();
  }, [empId]);

  // Extra fields functions
  const addExtraField = (type) => {
    let newField = { type, label: "", value: "" };
    if (type === "dropdown") newField.options = ["Option 1", "Option 2"];
    setExtraFields([...extraFields, newField]);
  };

  const updateExtraField = (index, key, val) => {
    const updated = [...extraFields];
    updated[index][key] = val;
    setExtraFields(updated);
  };

  const removeExtraField = (index) => {
    setExtraFields(extraFields.filter((_, i) => i !== index));
  };

  // Submit / Update
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!date || !hours || !task) return;

  setIsSubmitting(true);
  const hasFile = extraFields.some(f => f.type === "file" && f.value instanceof File);
  let dataToSend;
  let config = {};

  if (hasFile) {
    const formData = new FormData();
    // Send emp_id instead of employeeId for MongoDB lookup
    formData.append("emp_id", empId); // CHANGE THIS LINE
    formData.append("employeeName", employeeName);
    formData.append("date", date);
    formData.append("hours", Number(hours));
    formData.append("task", task);
    formData.append("status", status);

    extraFields.forEach((f, i) => {
      formData.append(`extraFields[${i}][label]`, f.label);
      formData.append(`extraFields[${i}][type]`, f.type);
      if (f.type === "file" && f.value instanceof File) {
        formData.append(`files`, f.value);
      } else if (f.type === "dropdown") {
        formData.append(`extraFields[${i}][options]`, JSON.stringify(f.options));
        formData.append(`extraFields[${i}][value]`, f.value);
      } else {
        formData.append(`extraFields[${i}][value]`, f.value);
      }
    });

    dataToSend = formData;
    config.headers = { "Content-Type": "multipart/form-data" };
  } else {
    // Send emp_id instead of employeeId
    dataToSend = { 
      emp_id: empId, // CHANGE THIS LINE - send emp_id instead
      employeeName, 
      date, 
      hours: Number(hours), 
      task, 
      status, 
      extraFields 
    };
  }

  try {
    let res;
    if (editingId) {
      res = await axios.put(`${API_BASE_URL}/api/timesheets/edit/${editingId}`, dataToSend, config);
      setTimesheets(prev =>
        prev.map(t => (t._id === editingId ? { ...t, ...res.data } : t))
      );
    } else {
      res = await axios.post(`${API_BASE_URL}/api/timesheets`, dataToSend, config);
      setTimesheets(prev => [res.data, ...prev]);
    }

    if (onTimesheetUpdate) onTimesheetUpdate(res.data);

    setDate(""); setHours(""); setTask(""); setStatus("Not Started"); setExtraFields([]); setEditingId(null);
    setActiveTab("records");
    toast.success(editingId ? "Timesheet updated successfully!" : "Timesheet submitted successfully!");
  } catch (err) {
    console.error("Error submitting/updating timesheet:", err.response?.data || err);
    toast.error(err.response?.data?.error || "Failed to submit timesheet");
  } finally {
    setIsSubmitting(false);
  }
};

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this timesheet?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/timesheets/${id}`);
      setTimesheets(timesheets.filter(t => t._id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // Edit
  const handleEdit = (t) => {
    setEditingId(t._id);
    setDate(t.date);
    setHours(t.hours);
    setTask(t.task);
    setStatus(t.status || "Not Started");
    setExtraFields(t.extraFields || []);
    setActiveTab("submission");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render dynamic field
  const renderField = (field, index) => {
    switch (field.type) {
      case "text": 
        return <input type="text" value={field.value} onChange={e => updateExtraField(index, "value", e.target.value)} className="form-input" />;
      case "notes": 
        return <textarea value={field.value} onChange={e => updateExtraField(index, "value", e.target.value)} className="form-textarea" />;
      case "dropdown": 
        return (
          <div className="dropdown-field">
            <select value={field.value} onChange={e => updateExtraField(index, "value", e.target.value)} className="form-select">
              <option value="">Select</option>
              {field.options?.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
            </select>
            <button type="button" onClick={() => {
              const newOption = prompt("Enter new option:");
              if (newOption) {
                const updated = [...extraFields];
                updated[index].options = [...(updated[index].options || []), newOption];
                setExtraFields(updated);
              }
            }} className="btn-add-option">+ Add Option</button>
          </div>
        );
      case "file": 
        return (
          <div className="file-input-wrapper">
            <input type="file" onChange={e => updateExtraField(index, "value", e.target.files[0])} className="form-file" />
            <span className="file-input-label">Choose file</span>
          </div>
        );
      case "date": 
        return <input type="date" value={field.value} onChange={e => updateExtraField(index, "value", e.target.value)} className="form-input" />;
      case "checkbox": 
        return (
          <label className="checkbox-label">
            <input type="checkbox" checked={field.value} onChange={e => updateExtraField(index, "value", e.target.checked)} />
            <span className="checkmark"></span>
          </label>
        );
      default: return null;
    }
  };

  // Filtered timesheets
  const filteredTimesheets = timesheets.filter(t =>
    t.employeeName.toLowerCase().includes(filter.name.toLowerCase()) &&
    t.task.toLowerCase().includes(filter.task.toLowerCase()) &&
    (filter.date === "" || new Date(t.date).toISOString().split("T")[0] === filter.date) &&
    (filter.status === "" || t.status === filter.status)
  );

  // Compute unique extra field labels
  const allExtraLabels = Array.from(new Set(timesheets.flatMap(t => t.extraFields?.map(f => f.label) || [])));

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'status-completed';
      case 'In Progress': return 'status-in-progress';
      case 'Not Started': return 'status-not-started';
      default: return 'status-default';
    }
  };

  return (
    <div className="timesheets-container">
      {/* Animated Background */}
      <div className="animated-bg">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      <div className="timesheets-content">
        {/* Header with Gradient Text */}
        <header className="timesheets-header">
          <h1 className="gradient-text">Timesheet Management</h1>
          <p className="subtitle">Track and manage your work hours efficiently</p>
        </header>

        {/* Navigation Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'submission' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('submission')}
            >
              <span className="tab-icon">📝</span>
              {editingId ? 'Edit Timesheet' : 'New Timesheet'}
            </button>
            <button 
              className={`tab ${activeTab === 'records' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('records')}
            >
              <span className="tab-icon">📊</span>
              My Timesheets ({timesheets.length})
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {activeTab === 'submission' && (
            <div className="form-section slide-in">
              <div className="form-card">
                <div className="form-header">
                  <h2 className="gradient-text">
                    {editingId ? 'Edit Timesheet Entry' : 'Create New Timesheet'}
                  </h2>
                  {/* <div className="form-steps">
                    <div className="step active">1. Basic Info</div>
                    <div className="step">2. Task Details</div>
                    <div className="step">3. Extra Fields</div>
                  </div> */}
                </div>

                <form onSubmit={handleSubmit} className="timesheet-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">Employee Name</span>
                        <input type="text" value={employeeName} readOnly className="form-input readonly" />
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">Employee ID</span>
                        <input type="text" value={empId} readOnly className="form-input readonly" />
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">Date <span className="required">*</span></span>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} required className="form-input" />
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">Hours <span className="required">*</span></span>
                        <input type="number" min="0" step="0.5" value={hours} onChange={e => setHours(e.target.value)} required className="form-input" />
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">Task <span className="required">*</span></span>
                        <input type="text" value={task} onChange={e => setTask(e.target.value)} required className="form-input" placeholder="Describe the task..." />
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">Status</span>
                        <select value={status} onChange={e => setStatus(e.target.value)} className="form-select">
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  {/* Extra Fields Section */}
                  <div className="extra-fields-section">
                    <div className="section-header">
                      <h3>Additional Information</h3>
                      <p>Add custom fields to your timesheet</p>
                    </div>

                    <div className="field-buttons">
                      <button type="button" onClick={() => addExtraField("text")} className="field-type-btn">
                        <span className="btn-icon">📝</span>
                        Text
                      </button>
                      <button type="button" onClick={() => addExtraField("notes")} className="field-type-btn">
                        <span className="btn-icon">📄</span>
                        Notes
                      </button>
                      <button type="button" onClick={() => addExtraField("dropdown")} className="field-type-btn">
                        <span className="btn-icon">🔽</span>
                        Dropdown
                      </button>
                      <button type="button" onClick={() => addExtraField("file")} className="field-type-btn">
                        <span className="btn-icon">📎</span>
                        File
                      </button>
                      <button type="button" onClick={() => addExtraField("date")} className="field-type-btn">
                        <span className="btn-icon">📅</span>
                        Date
                      </button>
                      <button type="button" onClick={() => addExtraField("checkbox")} className="field-type-btn">
                        <span className="btn-icon">☑️</span>
                        Checkbox
                      </button>
                    </div>

                    {extraFields.map((field, index) => (
                      <div key={index} className="extra-field-card">
                        <div className="field-header">
                          <input 
                            type="text" 
                            placeholder="Field Label" 
                            value={field.label} 
                            onChange={e => updateExtraField(index, "label", e.target.value)} 
                            required 
                            className="field-label-input"
                          />
                          <button type="button" onClick={() => removeExtraField(index)} className="btn-remove-field">
                            🗑️ Remove
                          </button>
                        </div>
                        <div className="field-input-container">
                          {renderField(field, index)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="form-actions">
                    {editingId && (
                      <button type="button" onClick={() => { setEditingId(null); setActiveTab('records'); }} className="btn-secondary">
                        Cancel Edit
                      </button>
                    )}
                    <button type="submit" disabled={isSubmitting} className="btn-primary">
                      {isSubmitting ? (
                        <>
                          <div className="spinner"></div>
                          {editingId ? 'Updating...' : 'Submitting...'}
                        </>
                      ) : (
                        editingId ? 'Update Timesheet' : 'Submit Timesheet'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'records' && (
            <div className="records-section slide-in">
              <div className="records-card">
                <div className="records-header">
                  <h2 className="gradient-text">Timesheet Records</h2>
                  <p>Manage and review your submitted timesheets</p>
                </div>

                {/* Filter Section */}
                <div className="filter-section">
                  <h3>Filter Records</h3>
                  <div className="filter-grid">
                    <div className="filter-group">
                      <label>Employee Name</label>
                      <input type="text" placeholder="Filter by name..." value={filter.name} onChange={e => setFilter({...filter, name: e.target.value})} className="form-input" />
                    </div>
                    <div className="filter-group">
                      <label>Task</label>
                      <input type="text" placeholder="Filter by task..." value={filter.task} onChange={e => setFilter({...filter, task: e.target.value})} className="form-input" />
                    </div>
                    <div className="filter-group">
                      <label>Date</label>
                      <input type="date" value={filter.date} onChange={e => setFilter({...filter, date: e.target.value})} className="form-input" />
                    </div>
                    <div className="filter-group">
                      <label>Status</label>
                      <select value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})} className="form-select">
                        <option value="">All Status</option>
                        <option value="Not Started">Not Started</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Timesheets Table */}
                <div className="table-container">
                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading timesheets...</p>
                    </div>
                  ) : filteredTimesheets.length > 0 ? (
                    <div className="table-wrapper">
                      <table className="timesheets-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Date</th>
                            <th>Hours</th>
                            <th>Task</th>
                            <th>Status</th>
                            {allExtraLabels.map((label, i) => (
                              <th key={i}>{label}</th>
                            ))}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTimesheets.map((t, index) => (
                            <tr key={t._id} className="fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                              <td>
                                <div className="employee-cell">
                                  <div className="employee-avatar">
                                    {t.employeeName.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="employee-name">{t.employeeName}</div>
                                    <div className="employee-id">{t.employeeId}</div>
                                  </div>
                                </div>
                              </td>
                              <td>{new Date(t.date).toLocaleDateString()}</td>
                              <td>
                                <span className="hours-badge">{t.hours}h</span>
                              </td>
                              <td className="task-cell">{t.task}</td>
                              <td>
                                <span className={`status-badge ${getStatusColor(t.status)}`}>
                                  {t.status}
                                </span>
                              </td>
                              
                              {allExtraLabels.map((label, i) => {
                                const field = t.extraFields?.find(f => f.label === label);
                                if (!field) return <td key={i}>-</td>;
                                if (field.type === "checkbox") return <td key={i}>{field.value ? "✅" : "❌"}</td>;
                                if (field.type === "file" && field.value) {
                                  const fileUrl = typeof field.value === "string" ? field.value : URL.createObjectURL(field.value);
                                  const fileName = typeof field.value === "string" ? field.value.split("/").pop() : field.value.name || "View File";
                                  return (
                                    <td key={i}>
                                      <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="file-link">
                                        📎 {fileName}
                                      </a>
                                    </td>
                                  );
                                }
                                return <td key={i} className="field-value">{field.value?.toString()}</td>;
                              })}

                              <td>
                                <div className="action-buttons">
                                  <button onClick={() => handleEdit(t)} className="btn-action btn-edit">
                                    ✏️ Edit
                                  </button>
                                  <button onClick={() => handleDelete(t._id)} className="btn-action btn-delete">
                                    🗑️ Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">📊</div>
                      <h3>No timesheets found</h3>
                      <p>No timesheets match your current filters, or you haven't submitted any timesheets yet.</p>
                      <button onClick={() => setActiveTab('submission')} className="btn-primary">
                        Create Your First Timesheet
                      </button>
                    </div>
                  )}
                </div>

                {/* Summary Stats */}
                {filteredTimesheets.length > 0 && (
                  <div className="summary-stats">
                    <div className="stat-card">
                      <div className="stat-value">{filteredTimesheets.length}</div>
                      <div className="stat-label">Total Entries</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">
                        {filteredTimesheets.reduce((sum, t) => sum + Number(t.hours), 0)}
                      </div>
                      <div className="stat-label">Total Hours</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">
                        {filteredTimesheets.filter(t => t.status === 'Completed').length}
                      </div>
                      <div className="stat-label">Completed</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Timesheets;